import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { X, Check, Loader2, ChevronDown, Coins } from "lucide-react";
import {
  Command,
  CommandInput,
  CommandList,
  CommandItem,
  CommandEmpty,
  CommandGroup,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

/**
 * Combobox restricted to Raw-Gold (Rg-*) products. Supports single OR multi select.
 * Fixed-height popover so results never reflow while typing.
 *
 * Props:
 *  - value:       single id (string|null) OR array of ids (string[]) when `multi`
 *  - valueLabel:  optional pre-resolved label(s) — string for single, {id: name} map for multi
 *  - onChange:    (single) (id|null, name|null) => void
 *                 (multi)  (ids[], names[]) => void
 *  - disabled
 *  - multi:       false (default) → single-select; true → multi-select w/ chips
 */
export default function RgSelect({ value, valueLabel, onChange, disabled, multi = false }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  // Cache: id -> name (accumulates as user browses / selects) so chips render
  // even when the selected id isn't in the current search results.
  const [nameCache, setNameCache] = useState({});
  const debounceRef = useRef();

  const selectedIds = multi ? (Array.isArray(value) ? value : []) : (value ? [value] : []);

  // Seed nameCache from valueLabel prop
  useEffect(() => {
    if (!valueLabel) return;
    if (multi && typeof valueLabel === "object") {
      setNameCache((c) => ({ ...valueLabel, ...c }));
    } else if (!multi && typeof valueLabel === "string" && value) {
      setNameCache((c) => ({ [value]: valueLabel, ...c }));
    }
  }, [valueLabel, multi, value]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get("/products/rg-candidates", { params: { search: q, limit: 25 } });
        const list = res.data || [];
        setOptions(list);
        // Extend the name cache from fetched options
        setNameCache((c) => {
          const next = { ...c };
          for (const o of list) next[o.id] = o.name;
          return next;
        });
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [q, open]);

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  const triggerLabel = useMemo(() => {
    if (multi) return null;
    if (!value) return null;
    return nameCache[value] || "Selected Rg product";
  }, [value, multi, nameCache]);

  const toggleOne = (id, name) => {
    if (multi) {
      const cur = new Set(selectedIds);
      if (cur.has(id)) cur.delete(id); else cur.add(id);
      const next = Array.from(cur);
      onChange(next, next.map((rid) => nameCache[rid] || name));
      // Keep the popover open for further multi selections
    } else {
      onChange(id, name);
      setOpen(false);
    }
  };

  const removeChip = (id) => {
    const next = selectedIds.filter((r) => r !== id);
    onChange(next, next.map((rid) => nameCache[rid] || ""));
  };

  return (
    <div className="space-y-2">
      {/* Multi-select chips */}
      {multi && selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedIds.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-md bg-amber-50 border border-amber-200 text-xs"
              data-testid={`rg-chip-${id}`}
            >
              <Coins className="h-3 w-3 text-amber-600" />
              <span className="text-amber-900 font-medium max-w-[180px] truncate">
                {nameCache[id] || "Loading…"}
              </span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeChip(id)}
                  className="h-4 w-4 rounded hover:bg-amber-200 flex items-center justify-center text-amber-700"
                  title="Remove"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              className="flex-1 h-11 justify-between font-normal px-3 hover:bg-white"
              data-testid="rg-select-trigger"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Coins className="h-4 w-4 text-amber-600 flex-shrink-0" />
                {multi ? (
                  <span className="text-gray-500 text-sm">
                    {selectedIds.length === 0
                      ? "Search & add Raw-Gold products…"
                      : `Add another (${selectedIds.length} selected)`}
                  </span>
                ) : value ? (
                  <span className="text-gray-900 truncate text-left" title={triggerLabel}>
                    {triggerLabel}
                  </span>
                ) : (
                  <span className="text-gray-400">Search & select a Raw-Gold product…</span>
                )}
              </div>
              <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
            </Button>
          </PopoverTrigger>

          <PopoverContent
            className="w-[--radix-popover-trigger-width] p-0 shadow-lg border-gray-200"
            align="start"
            sideOffset={4}
          >
            <Command shouldFilter={false} className="bg-white">
              <CommandInput
                value={q}
                onValueChange={setQ}
                placeholder="Type to search Rg products (e.g. '24 Gopi')…"
                className="h-11 text-sm"
                data-testid="rg-select-search"
              />
              <CommandList className="h-[280px] max-h-[280px] overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    <span className="text-xs">Searching…</span>
                  </div>
                ) : (
                  <>
                    <CommandEmpty className="text-center text-xs text-gray-400 py-8">
                      {q ? `No Rg products match "${q}"` : "Start typing to search…"}
                    </CommandEmpty>
                    {options.length > 0 && (
                      <CommandGroup heading={`${options.length} match${options.length === 1 ? "" : "es"}`} className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-widest [&_[cmdk-group-heading]]:text-gray-400">
                        {options.map((o) => {
                          const isSelected = selectedIds.includes(o.id);
                          return (
                            <CommandItem
                              key={o.id}
                              value={o.id}
                              onSelect={() => toggleOne(o.id, o.name)}
                              className={`cursor-pointer px-3 py-2 aria-selected:bg-amber-50/60 flex items-start gap-2 ${
                                isSelected && multi ? "bg-amber-50/40" : ""
                              }`}
                              data-testid={`rg-option-${o.id}`}
                            >
                              {isSelected ? (
                                <Check className="h-4 w-4 text-[#9B1C1C] flex-shrink-0 mt-0.5" />
                              ) : (
                                <span className="w-4 flex-shrink-0" />
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="text-sm text-gray-900 truncate leading-tight">{o.name}</p>
                                <p className="text-[11px] text-gray-500 font-mono truncate">{o.code}</p>
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    )}
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Clear-all — visible only in single mode; multi mode uses per-chip X */}
        {!multi && value && !disabled && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange(null, null)}
            className="h-11 px-2.5 text-gray-400 hover:text-red-700 hover:bg-red-50"
            title="Clear mapping"
            data-testid="rg-select-clear"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
